import type { FoodSafetySettings, Renewal, RenewalPut, Temperature, Vehicle, VehicleLogCreate } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, newIdempotencyKey } from '../api';
import { useSite } from '../sites';
import { useAuth } from '../auth';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, parseTyped, todayIso } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/** "Expired", "Today", "1 day", "10 days" */
export function daysLeftLabel(r: Pick<Renewal, 'status' | 'daysLeft'>, t: (k: 'renewalExpired' | 'renewalToday' | 'renewalOneDay' | 'renewalDaysLeft') => string): string {
  if (r.status === 'expired') return t('renewalExpired');
  if (r.daysLeft === 0) return t('renewalToday');
  if (r.daysLeft === 1) return t('renewalOneDay');
  return t('renewalDaysLeft').replace('{n}', String(r.daysLeft));
}

const KINDS: RenewalPut['kind'][] = ['license', 'permit', 'insurance', 'registration', 'inspection', 'certification', 'other'];

/**
 * Compliance, food safety and the fleet (decision 112): what runs out and when, the temperature log an inspector asks
 * for, and the trucks with their fuel and service.
 */
export function CompliancePage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const office = me?.role === 'owner' || me?.role === 'office';
  // the log and the trucks are the floor's and the drivers' (decision 184): anyone else is told plainly
  const floor = office || me?.role === 'warehouse' || me?.role === 'driver';
  if (!floor) {
    return (
      <div className="stack">
        <PageHead title={t('complianceTitle')} back="/" />
        <p className="alert alert-error" data-testid="not-for-role">{t('notForRole')}</p>
      </div>
    );
  }
  return (
    <div className="stack">
      <PageHead title={t('complianceTitle')} back="/" />
      <p className="muted">{t('complianceHint')}</p>
      <TemperatureCard canRead={office || me?.role === 'warehouse'} canVoid={office || me?.role === 'warehouse'} owner={me?.role === 'owner'} />
      {office ? <RenewalsCard /> : null}
      <FleetCard canEdit={office} canLog={office || me?.role === 'driver'} />
    </div>
  );
}

function RenewalsCard() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: qk.renewals, queryFn: ({ signal }) => api.renewals(signal) });
  const [editing, setEditing] = useState<Renewal | 'new' | null>(null);
  const archive = useMutation({ mutationFn: (id: string) => api.archiveRenewal(id), onSuccess: () => void qc.invalidateQueries({ queryKey: qk.renewals }) });
  const badge = (r: Renewal) => (r.status === 'expired' ? 'badge badge-danger' : r.status === 'due' ? 'badge badge-warn' : 'badge badge-ok');
  return (
    <section className="card wide stack-sm" data-testid="renewals">
      <h2>{t('renewalsTitle')}</h2>
      <QueryView query={query}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noRenewals')}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('renewalKindCol')}</th>
                    <th>{t('renewalHolderCol')}</th>
                    <th>{t('expiresCol')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid="renewal">
                      <td>
                        {r.name}
                        {r.note ? <span className="muted"> · {r.note}</span> : null}
                      </td>
                      <td>{t(`renewal_${r.kind}`)}</td>
                      <td>{r.holder ?? '—'}</td>
                      <td>
                        {formatDate(r.expiresOn, intlLocale, 'date')}{' '}
                        <span className={badge(r)}>{daysLeftLabel(r, t)}</span>
                      </td>
                      <td className="contact-actions">
                        <button type="button" className="btn btn-ghost" onClick={() => setEditing(r)}>
                          {t('renewBtn')}
                        </button>
                        <button type="button" className="btn btn-ghost" disabled={archive.isPending} onClick={() => window.confirm(t('renewalArchiveAsk')) && archive.mutate(r.id)}>
                          {t('archive')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryView>
      {editing ? (
        <RenewalForm key={editing === 'new' ? 'new' : editing.id} renewal={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      ) : (
        <button type="button" className="btn btn-secondary" onClick={() => setEditing('new')}>
          + {t('addRenewal')}
        </button>
      )}
      {archive.error ? <ErrorBox error={archive.error} /> : null}
    </section>
  );
}

function RenewalForm({ renewal, onDone }: { renewal: Renewal | null; onDone: () => void }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: renewal?.name ?? '', kind: renewal?.kind ?? 'license', holder: renewal?.holder ?? '', expiresOn: renewal?.expiresOn ?? addDays(todayIso(), 365), remindDays: String(renewal?.remindDays ?? 30), note: renewal?.note ?? '' });
  const [key] = useState(() => newIdempotencyKey());
  const remind = Number(f.remindDays);
  const ok = f.name.trim() && f.expiresOn && Number.isInteger(remind) && remind >= 0 && remind <= 365;
  const save = useMutation({
    mutationFn: () => api.saveRenewal(renewal?.id ?? null, { name: f.name.trim(), kind: f.kind, holder: f.holder.trim() || null, expiresOn: f.expiresOn, remindDays: remind, note: f.note.trim() || null }, key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.renewals });
      void qc.invalidateQueries({ queryKey: qk.vehicles });
      onDone();
    },
  });
  return (
    <form
      className="stack-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) save.mutate();
      }}
    >
      <div className="form-grid">
        <label className="field">
          <span>{t('name')}</span>
          <input type="text" value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="field">
          <span>{t('renewalKindCol')}</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as RenewalPut['kind'] })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`renewal_${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('renewalHolderCol')}</span>
          <input type="text" value={f.holder} maxLength={80} placeholder={t('renewalHolderHint')} onChange={(e) => setF({ ...f, holder: e.target.value })} />
        </label>
        <label className="field">
          <span>{t('expiresCol')}</span>
          <input type="date" value={f.expiresOn} onChange={(e) => setF({ ...f, expiresOn: e.target.value })} />
        </label>
        <label className="field">
          <span>{t('remindDaysLabel')}</span>
          <input type="text" inputMode="numeric" value={f.remindDays} onChange={(e) => setF({ ...f, remindDays: e.target.value })} style={{ width: 90 }} />
        </label>
        <label className="field">
          <span>{t('notes')}</span>
          <input type="text" value={f.note} maxLength={500} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </label>
      </div>
      <div className="picker-head">
        <button type="button" className="btn btn-secondary" onClick={onDone}>
          {t('back')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={save.isPending || !ok}>
          {t('save')}
        </button>
      </div>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </form>
  );
}

function TemperatureCard({ canRead, canVoid, owner }: { canRead: boolean; canVoid: boolean; owner: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const { siteId } = useSite();
  const [key, setKey] = useState(() => newIdempotencyKey());
  const voidIt = useMutation({ mutationFn: (v: { id: string; note: string }) => api.voidTemperature(v.id, v.note), onSuccess: () => void qc.invalidateQueries({ queryKey: ['compliance', 'temperatures'] }) });
  const to = todayIso();
  const from = addDays(to, -6);
  const log = useQuery({ queryKey: qk.temperatures(from, to), queryFn: ({ signal }) => api.temperatures({ from, to }, signal), enabled: canRead });
  const limits = useQuery({ queryKey: qk.foodSafety, queryFn: ({ signal }) => api.foodSafety(signal) });
  const vehicles = useQuery({ queryKey: qk.vehicles, queryFn: ({ signal }) => api.vehicles(signal) });
  const [place, setPlace] = useState<Temperature['place']>('cooler');
  const [temp, setTemp] = useState('');
  const [condition, setCondition] = useState<'' | 'good' | 'fair' | 'poor'>('');
  const [vehicleId, setVehicleId] = useState('');
  const [note, setNote] = useState('');
  const [last, setLast] = useState<Temperature | null>(null);
  // a reefer that froze reads below zero
  const f = parseTyped(temp, { negative: true });
  const ok = f !== null && f >= -40 && f <= 140 && (place !== 'truck' || vehicleId);
  const save = useMutation({
    mutationFn: () => api.logTemperature({ place, tempF: Math.round(f! * 10) / 10, ...(place !== 'truck' && siteId ? { siteId } : {}), ...(place === 'dock' && condition ? { condition } : {}), ...(place === 'truck' && vehicleId ? { vehicleId } : {}), ...(note.trim() ? { note: note.trim() } : {}) }, key),
    onSuccess: (r) => {
      setLast(r);
      setKey(newIdempotencyKey());
      setTemp('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['compliance', 'temperatures'] });
    },
  });
  const l = limits.data;
  return (
    <section className="card stack-sm" data-testid="temperatures">
      <h2>{t('temperaturesTitle')}</h2>
      {l ? (
        <p className="muted">
          {t('temperatureLimits').replace('{cmin}', String(l.coolerMinF)).replace('{cmax}', String(l.coolerMaxF)).replace('{dmin}', String(l.dockMinF)).replace('{dock}', String(l.dockMaxF)).replace('{tmin}', String(l.truckMinF)).replace('{truck}', String(l.truckMaxF))}
        </p>
      ) : null}
      {owner && l ? <LimitsForm limits={l} /> : null}
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) save.mutate();
        }}
      >
        <div className="chips" role="group" aria-label={t('tempPlace')}>
          {(['cooler', 'dock', 'truck'] as const).map((p) => (
            <button key={p} type="button" className="chip" aria-pressed={place === p} onClick={() => setPlace(p)}>
              {t(`temp_${p}`)}
            </button>
          ))}
        </div>
        <div className="form-grid">
          <label className="field">
            <span>{t('tempF')}</span>
            <input type="text" inputMode="decimal" value={temp} aria-invalid={(temp.trim() !== '' && f === null) || undefined} onChange={(e) => setTemp(e.target.value)} style={{ width: 100 }} />
          </label>
          {place === 'dock' ? (
            <label className="field">
              <span>{t('loadCondition')}</span>
              <select value={condition} onChange={(e) => setCondition(e.target.value as typeof condition)}>
                <option value="">—</option>
                {(['good', 'fair', 'poor'] as const).map((c) => (
                  <option key={c} value={c}>
                    {t(`condition_${c}`)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {place === 'truck' ? (
            <label className="field">
              <span>{t('vehicle')}</span>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">—</option>
                {(vehicles.data ?? []).filter((v) => v.active).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="field">
            <span>{t('notes')}</span>
            <input type="text" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={save.isPending || !ok}>
          {t('logReading')}
        </button>
      </form>
      {last ? (
        <p className={last.outOfRange ? 'field-error' : 'note'} role="status">
          {last.outOfRange ? `⚠ ${t('tempOutOfRange')}` : `✓ ${t('saved')}`} · {t(`temp_${last.place}`)} {last.tempF}°F
        </p>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
      {voidIt.error ? <ErrorBox error={voidIt.error} /> : null}
      {canRead ? (
        <QueryView query={log}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty>{t('noReadings')}</Empty>
            ) : (
              <ul className="list" data-testid="temp-log">
                {rows.map((r) => (
                  <li key={r.id} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {t(`temp_${r.place}`)}
                        {r.vehicleName ? ` · ${r.vehicleName}` : ''}
                        {r.vendorName ? ` · ${r.vendorName}` : ''}
                        {r.siteName ? ` · ${r.siteName}` : ''}
                        {r.outOfRange && !r.voided ? <span className="badge badge-danger">{t('tempOutOfRange')}</span> : null}
                        {r.voided ? <span className="badge">{t('takenBack')}</span> : null}
                        {r.enteredLate ? <span className="badge badge-warn">{t('enteredLate')}</span> : null}
                      </span>
                      <span className="row-sub">
                        {new Date(r.takenAt).toLocaleString(intlLocale, { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        {r.byName ? ` · ${r.byName}` : ''}
                        {r.condition ? ` · ${t(`condition_${r.condition}`)}` : ''}
                        {r.note ? ` · ${r.note}` : ''}
                        {r.voidNote ? ` · ${t('takenBack')}: ${r.voidNote}` : ''}
                      </span>
                    </span>
                    <span className={r.voided ? 'row-end-value muted' : r.outOfRange ? 'row-end-value owed' : 'row-end-value'}>{r.tempF}°F</span>
                    {canVoid && !r.voided ? (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={voidIt.isPending}
                        onClick={() => {
                          const why = window.prompt(t('takeBackWhy'));
                          if (why && why.trim()) voidIt.mutate({ id: r.id, note: why.trim() });
                        }}
                      >
                        {t('takeBack')}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )
          }
        </QueryView>
      ) : null}
    </section>
  );
}

function FleetCard({ canEdit, canLog }: { canEdit: boolean; canLog: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: qk.vehicles, queryFn: ({ signal }) => api.vehicles(signal) });
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => api.addVehicle({ name: name.trim(), ...(plate.trim() ? { plate: plate.trim() } : {}) }),
    onSuccess: () => {
      setName('');
      setPlate('');
      void qc.invalidateQueries({ queryKey: qk.vehicles });
    },
  });
  return (
    <section className="card stack-sm" data-testid="fleet">
      <h2>{t('fleetTitle')}</h2>
      <QueryView query={query}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noVehicles')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((v) => (
                <li key={v.id} className="stack-sm">
                  <button type="button" className="row" onClick={() => setOpen(open === v.id ? null : v.id)} aria-expanded={open === v.id}>
                    <span className="row-main">
                      <span className="row-title">
                        {v.name}
                        {v.plate ? <span className="muted"> · {v.plate}</span> : null}
                        {!v.active ? <span className="badge">{t('inactive')}</span> : null}
                        {v.renewalsDue > 0 ? <span className="badge badge-warn">{t('renewalsDueBadge').replace('{n}', String(v.renewalsDue))}</span> : null}
                      </span>
                      <span className="row-sub">
                        {t('odometer')} {v.lastOdometer === null ? '—' : v.lastOdometer.toLocaleString(intlLocale)} · {t('lastService')} {v.lastService ? formatDate(v.lastService, intlLocale, 'date') : '—'}
                      </span>
                    </span>
                    {v.fuel30 !== null ? (
                      <span className="row-end">
                        <span className="row-end-value">{formatMoney(v.fuel30, intlLocale)}</span>
                        <span className="row-end-label">{t('fuel30')}</span>
                      </span>
                    ) : null}
                  </button>
                  {open === v.id ? <VehicleLogs vehicle={v} canLog={canLog} canVoid={canEdit} /> : null}
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
      {canEdit ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) add.mutate();
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span>{t('vehicleName')}</span>
              <input type="text" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('plate')}</span>
              <input type="text" value={plate} maxLength={20} onChange={(e) => setPlate(e.target.value)} />
            </label>
          </div>
          <button type="submit" className="btn btn-secondary" disabled={add.isPending || !name.trim()}>
            + {t('addVehicle')}
          </button>
        </form>
      ) : null}
      {add.error ? <ErrorBox error={add.error} /> : null}
    </section>
  );
}

function VehicleLogs({ vehicle, canLog, canVoid }: { vehicle: Vehicle; canLog: boolean; canVoid: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [key, setKey] = useState(() => newIdempotencyKey());
  const voidIt = useMutation({
    mutationFn: (v: { id: string; note: string }) => api.voidVehicleLog(v.id, v.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.vehicleLogs(vehicle.id) });
      void qc.invalidateQueries({ queryKey: qk.vehicles });
    },
  });
  const logs = useQuery({ queryKey: qk.vehicleLogs(vehicle.id), queryFn: ({ signal }) => api.vehicleLogs(vehicle.id, signal) });
  const [kind, setKind] = useState<VehicleLogCreate['kind']>('fuel');
  const [odo, setOdo] = useState('');
  const [gallons, setGallons] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const n = { odo: parseTyped(odo), gallons: parseTyped(gallons), amount: parseTyped(amount) };
  const bad = (s: string, v: number | null) => s.trim() !== '' && (v === null || v < 0);
  const ok =
    !bad(odo, n.odo) && !bad(gallons, n.gallons) && !bad(amount, n.amount) &&
    (kind === 'fuel' ? n.gallons !== null || n.amount !== null : kind === 'odometer' ? n.odo !== null : note.trim() !== '');
  const save = useMutation({
    mutationFn: () =>
      api.logVehicle(vehicle.id, {
        kind,
        ...(n.odo !== null && odo.trim() ? { odometer: Math.round(n.odo) } : {}),
        ...(n.gallons !== null && gallons.trim() && kind === 'fuel' ? { gallons: Math.round(n.gallons * 100) / 100 } : {}),
        ...(n.amount !== null && amount.trim() && kind !== 'odometer' ? { amount: Math.round(n.amount * 100) / 100 } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }, key),
    onSuccess: () => {
      setKey(newIdempotencyKey());
      setOdo('');
      setGallons('');
      setAmount('');
      setNote('');
      void qc.invalidateQueries({ queryKey: qk.vehicleLogs(vehicle.id) });
      void qc.invalidateQueries({ queryKey: qk.vehicles });
    },
  });
  return (
    <div className="stack-sm" data-testid="vehicle-logs">
      {canLog ? (
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) save.mutate();
        }}
      >
        <div className="chips" role="group" aria-label={t('logKind')}>
          {(['fuel', 'service', 'odometer'] as const).map((k) => (
            <button key={k} type="button" className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {t(`vlog_${k}`)}
            </button>
          ))}
        </div>
        <div className="form-grid">
          <label className="field">
            <span>{t('odometer')}</span>
            <input type="text" inputMode="numeric" value={odo} aria-invalid={bad(odo, n.odo) || undefined} onChange={(e) => setOdo(e.target.value)} style={{ width: 110 }} />
          </label>
          {kind === 'fuel' ? (
            <label className="field">
              <span>{t('gallons')}</span>
              <input type="text" inputMode="decimal" value={gallons} aria-invalid={bad(gallons, n.gallons) || undefined} onChange={(e) => setGallons(e.target.value)} style={{ width: 90 }} />
            </label>
          ) : null}
          {kind !== 'odometer' ? (
            <label className="field">
              <span>{t('amount')}</span>
              <input type="text" inputMode="decimal" value={amount} aria-invalid={bad(amount, n.amount) || undefined} onChange={(e) => setAmount(e.target.value)} style={{ width: 100 }} />
            </label>
          ) : null}
          <label className="field">
            <span>{kind === 'service' ? t('serviceWhat') : t('notes')}</span>
            <input type="text" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={save.isPending || !ok}>
          {t('save')}
        </button>
        {save.error ? <ErrorBox error={save.error} /> : null}
      </form>
      ) : null}
      {voidIt.error ? <ErrorBox error={voidIt.error} /> : null}
      <QueryView query={logs}>
        {(rows) =>
          rows.length === 0 ? null : (
            <ul className="list">
              {rows.slice(0, 10).map((r) => (
                <li key={r.id} className="row">
                  <span className="row-main">
                    <span className="row-title">
                      {t(`vlog_${r.kind}`)}
                      {r.note ? ` · ${r.note}` : ''}
                      {r.voided ? <span className="badge">{t('takenBack')}</span> : null}
                    </span>
                    <span className="row-sub">
                      {formatDate(r.loggedOn, intlLocale, 'date')}
                      {r.odometer !== null ? ` · ${r.odometer.toLocaleString(intlLocale)}` : ''}
                      {r.gallons !== null ? ` · ${r.gallons} gal` : ''}
                      {r.byName ? ` · ${r.byName}` : ''}
                    </span>
                  </span>
                  <span className={r.voided ? 'row-end-value muted' : 'row-end-value'}>{r.amount !== null ? formatMoney(r.amount, intlLocale) : ''}</span>
                  {canVoid && !r.voided ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={voidIt.isPending}
                      onClick={() => {
                        const why = window.prompt(t('takeBackWhy'));
                        if (why && why.trim()) voidIt.mutate({ id: r.id, note: why.trim() });
                      }}
                    >
                      {t('takeBack')}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

/** The owner's temperature limits (readings already taken keep theirs). */
function LimitsForm({ limits }: { limits: FoodSafetySettings }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<Record<keyof FoodSafetySettings, string>>(() => Object.fromEntries(Object.entries(limits).map(([k, v]) => [k, String(v)])) as Record<keyof FoodSafetySettings, string>);
  const nums = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, parseTyped(v, { negative: true })])) as Record<keyof FoodSafetySettings, number | null>;
  const ok = Object.values(nums).every((n) => n !== null) && nums.coolerMinF! < nums.coolerMaxF! && nums.dockMinF! < nums.dockMaxF! && nums.truckMinF! < nums.truckMaxF!;
  const save = useMutation({
    mutationFn: () => api.saveFoodSafety(nums as FoodSafetySettings),
    onSuccess: (v) => {
      qc.setQueryData(qk.foodSafety, v);
      setOpen(false);
    },
  });
  if (!open)
    return (
      <button type="button" className="btn btn-ghost" onClick={() => setOpen(true)}>
        {t('changeLimits')}
      </button>
    );
  const field = (k: keyof FoodSafetySettings, label: string) => (
    <label className="field" key={k}>
      <span>{label}</span>
      <input type="text" inputMode="decimal" value={f[k]} aria-invalid={nums[k] === null || undefined} onChange={(e) => setF({ ...f, [k]: e.target.value })} style={{ width: 80 }} />
    </label>
  );
  return (
    <form
      className="stack-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) save.mutate();
      }}
    >
      <div className="form-grid">
        {field('coolerMinF', `${t('temp_cooler')} min`)}
        {field('coolerMaxF', `${t('temp_cooler')} max`)}
        {field('dockMinF', `${t('temp_dock')} min`)}
        {field('dockMaxF', `${t('temp_dock')} max`)}
        {field('truckMinF', `${t('temp_truck')} min`)}
        {field('truckMaxF', `${t('temp_truck')} max`)}
      </div>
      <p className="muted">{t('limitsHint')}</p>
      <button type="submit" className="btn btn-primary" disabled={save.isPending || !ok}>
        {t('save')}
      </button>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </form>
  );
}
